<script setup lang="ts">
import type { ToasterProps } from 'vue-sonner';
import { breakpointsTailwind } from '@vueuse/core';
import { XIcon } from 'lucide-vue-next';
import { Toaster as Sonner } from 'vue-sonner';
import 'vue-sonner/style.css';

const props = withDefaults(defineProps<ToasterProps>(), {
  closeButton: true,
  duration: 5000,
  swipeDirections: () => ['top', 'right', 'left'],
});
const delegatedProps = reactiveOmit(props, ['toastOptions', 'position', 'containerAriaLabel']);

const { t } = useI18n();

// The app's mobile-first SSR width, on purpose: rendered as top-right, Sonner sets `top`, the mobile classes
// below set `bottom`, and a fixed box with both covers the whole viewport until hydration flips it —
// every tap on a phone's home was swallowed for as long as the scripts took to arrive.
const breakpoints = useBreakpoints(breakpointsTailwind);
const isXl = breakpoints.greaterOrEqual('xl');
const position = computed(() => (isXl.value ? 'top-right' : 'bottom-right'));
</script>

<template>
  <Sonner
    class="toaster group !bottom-4 !left-4 !right-4 !w-auto md:!bottom-6 md:!left-6 md:!right-6 xl:!bottom-auto xl:!left-auto xl:!right-4 xl:!top-4 xl:!w-96"
    :position="position"
    :container-aria-label="t('components.common.notifications')"
    :toast-options="{
      closeButtonAriaLabel: t('components.common.closeToast'),
      classes: {
        toast:
          'group toast group-[.toaster]:bg-background group-[.toaster]:text-text-primary-900 group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:text-sm group-[.toaster]:leading-none group-[.toaster]:!items-start group-[.toaster]:!justify-between group-[.toaster]:!w-full group-[.toaster]:md:!max-w-96 [&_[data-icon]]:!hidden [&_[data-title]]:!font-normal',
        description: 'group-[.toast]:text-text-secondary-600',
        actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-text-inverse',
        cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-text-secondary-600',
        closeButton:
          'group-[.toast]:!static group-[.toast]:!transform-none group-[.toast]:!order-last group-[.toast]:!bg-transparent group-[.toast]:!border-transparent',
        error:
          'group-[.toaster]:border-red-200 group-[.toaster]:bg-red-50 group-[.toaster]:text-red-800 [&_[data-title]]:!text-red-800 [&_[data-description]]:!text-red-800 [&_[data-close-button]]:!text-red-800/70',
      },
    }"
    v-bind="delegatedProps"
  >
    <template #error-icon />
    <template #close-icon>
      <XIcon class="size-4" />
    </template>
  </Sonner>
</template>
